import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandLayout from "@/components/layout/BrandLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { FileText, CheckCircle, Clock, ArrowRight } from "lucide-react";

const BrandDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    pending_count: 0,
    approved_count: 0
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, submissionsRes] = await Promise.all([
          axios.get(`${API}/brand/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/brand/submissions`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setStats(statsRes.data);
        setRecentSubmissions(submissionsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const statCards = [
    { label: "Pending Review", value: stats.pending_count, icon: Clock, color: "bg-[#F58220]" },
    { label: "Approved", value: stats.approved_count, icon: CheckCircle, color: "bg-green-500" },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending_brand_approval: "bg-[#F58220]/20 text-[#F58220]",
      approved: "bg-green-500/20 text-green-400",
    };
    const labels = {
      pending_brand_approval: "Pending Review",
      approved: "Approved",
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold uppercase tracking-wider ${styles[status] || "bg-gray-500/20 text-gray-400"}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <BrandLayout>
      <div className="animate-fade-in" data-testid="brand-dashboard">
        <div className="mb-8">
          <h1 className="text-4xl font-heading text-white mb-2">BRAND DASHBOARD</h1>
          <p className="text-gray-400">Review and approve agency-verified submissions</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
          {statCards.map((stat) => (
            <div 
              key={stat.label} 
              className="bg-gray-900 border border-gray-800 p-6 hover:border-[#F58220] transition-colors duration-300"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-4xl font-heading text-white">{loading ? "-" : stat.value}</p>
                  <p className="text-sm text-gray-400 uppercase tracking-wider mt-1">{stat.label}</p>
                </div>
                <div className={`${stat.color} p-3 text-white`}>
                  <stat.icon size={24} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Submissions */}
        <div className="bg-gray-900 border border-gray-800">
          <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="font-heading text-xl text-white">SUBMISSIONS FOR REVIEW</h2>
            <Link 
              to="/brand/submissions" 
              className="text-[#F58220] hover:underline flex items-center gap-1 text-sm font-medium"
            >
              View All <ArrowRight size={16} />
            </Link>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : recentSubmissions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No submissions pending review</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Artist</th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Venue</th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Date</th>
                    <th className="text-left font-heading uppercase text-xs tracking-wider text-gray-400 py-4 px-4">Status</th>
                    <th className="py-4 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {recentSubmissions.map((submission) => (
                    <tr key={submission.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-4 px-4 font-medium text-white">{submission.artist_name}</td>
                      <td className="py-4 px-4 text-gray-400">{submission.venue}</td>
                      <td className="py-4 px-4 text-gray-400">{submission.date}</td>
                      <td className="py-4 px-4">{getStatusBadge(submission.status)}</td>
                      <td className="py-4 px-4">
                        <Link 
                          to={`/brand/submissions/${submission.id}`}
                          className="text-[#F58220] hover:underline text-sm font-medium"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </BrandLayout>
  );
};

export default BrandDashboard;
